import classNames from "classnames";
import type { JSXElementConstructor, ReactNode } from "react";
import { styled, Card as MuiCard } from "~/common/mui";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CardProps<T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>> =
  (T extends keyof JSX.IntrinsicElements
    ? JSX.IntrinsicElements[T]
    : {
        className?: string;
        onClick?: () => void;
        children?: ReactNode;
      }) & {
    as?: T;
    variant?: "rounded" | "square";
    innerClassName?: string;
    disabled?: boolean;
    active?: boolean;
    error?: boolean;
    disableHover?: boolean;
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Card = <T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>>({
  // @ts-ignore
  as = "div",
  variant = "rounded",
  disabled,
  active,
  className,
  innerClassName,
  disableHover,
  error,
  ...rest
}: CardProps<T>) => {
  const cls = classNames(
    className,
    innerClassName,
    "scha__lc__card",
    `scha__lc__card--${variant}`,
    disabled && "scha__lc__card--disabled",
    active && "scha__lc__card--active",
    error && "scha__lc__card--error",
    rest.onClick && !disableHover && "scha__lc__card--has-hover",
  );

  return (
    // @ts-ignore
    <LcCardRoot component={as} className={cls} {...rest} disabled={as === "button" ? disabled : undefined} />
  );
};


const LcCardRoot = styled("div")(({ theme }) => ({
  position: "relative",
  display: "block",
  "&.scha__lc__card--has-hover": {
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
      pointerEvents: "none",
      zIndex: 1,
    },
    "&:not(.scha__lc__card--disabled)": {
      cursor: "pointer",
      "&:hover": {
        "&:before": {
          background: theme.palette.action.hover,
        },
      },
    },
  },
  "&:after": {
    content: '""',
    border: `1px solid ${theme.palette.divider}`,
    position: "absolute",
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    zIndex: 1,
    pointerEvents: "none",
  },
  "&&.scha__lc__card--rounded": {
    borderRadius: theme.spacing(1),
    "&:after": {
      borderRadius: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
    },
  },
  "&&.scha__lc__card--active": {
    "&:after": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& > a:not(.scha__link, .MuiButton-root)": {
    color: theme.palette.text.primary,
    "&:hover": {
      color: theme.palette.text.primary,
      cursor: "pointer",
    },
  },
  "&&.scha__lc__card--error": {
    "&:after": {
      borderColor: theme.palette.error.main,
    },
  },
  "&&.scha__lc__card--disabled": {
    pointerEvents: "none",
    opacity: 0.38,
  },
  "& .scha__lc__card__checkmark": {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    borderRadius: "50%",
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.primary.contrastText,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "none",
    visibility: "hidden",
    "&&.scha__lc__card__checkmark--checked": {
      borderColor: theme.palette.primary.main,
      background: theme.palette.primary.main,
    },
  },
  "& .scha__lc__card__contents": {
    padding: theme.spacing(2),
    color: theme.palette.text.primary,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
  },
  "& .scha__lc__card__image": {
    position: "relative",
  },
  "& .scha__lc__card__standard": {
    marginTop: theme.spacing(1.25),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.625),
    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  "&.scha__lc__card--active .scha__lc__card__checkmark, &.scha__lc__card--has-hover:not(.scha__lc__card--disabled):hover .scha__lc__card__checkmark": {
    visibility: "visible",
  },
  "& .scha__lc__card__headline": {
    ...theme.typography.titleMedium,
    lineHeight: "20px",
    margin: 0,
    display: "flex",
    alignItems: "flex-start",
  },
  "& .scha__lc__card__subheadline, & .scha__lc__card__headline > .scha__lc__external-label > .figure > .legend_figure": {
    marginTop: theme.spacing(0.625),
    marginBottom: 0,
    ...theme.typography.titleSmall,
  },
}));
